export class ModeSelector {
    constructor(containerId, onModeChange, onConfigChange) {
        this.selectedMode = 'time';
        const container = document.getElementById(containerId);
        if (!container) {
            throw new Error(`Container with id "${containerId}" not found`);
        }
        this.container = container;
        this.onModeChange = onModeChange;
        this.onConfigChange = onConfigChange;
        this.config = this.getDefaultConfig();
        this.render();
    }
    setMode(mode) {
        this.selectedMode = mode;
        this.render();
    }
    setConfig(config) {
        // Ensure mode is valid, default to 'time' (aeg mode) if not found or invalid
        const validModes = ['time', 'words', 'paragraphs', 'quote', 'zen'];
        if (!config.mode || !validModes.includes(config.mode)) {
            config.mode = 'time';
        }
        this.config = config;
        this.selectedMode = config.mode;
        this.render();
    }
    render() {
        const modes = [
            { mode: 'time', symbol: 'O', label: 'aeg', icon: '🕐' },
            { mode: 'words', symbol: 'A', label: 'sõnad' },
            { mode: 'paragraphs', symbol: 'P', label: 'lõigud', icon: '📝' },
        ];
        this.container.innerHTML = `
      <div class="flex flex-col gap-3 md:gap-4">
        <div class="flex flex-wrap items-center gap-2 md:gap-3 lg:gap-4 justify-center md:justify-start">
          ${modes.map(({ mode, symbol, label, icon }) => `
            <button class="mode-btn group relative px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 focus:outline-none flex items-center gap-2 min-h-[44px] touch-manipulation ${this.selectedMode === mode
            ? 'text-yellow-400 bg-yellow-400/10 border-2 border-yellow-400/30 shadow-lg shadow-yellow-400/20 scale-105'
            : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50 border-2 border-transparent hover:border-gray-700 active:scale-95'}" 
                    data-mode="${mode}"
                    aria-label="${label}">
              ${icon ? `<span class="text-lg md:text-xl transition-transform duration-300 group-hover:scale-110">${icon}</span>` : `<span class="font-mono text-sm md:text-base">${symbol}</span>`}
              <span class="font-medium text-base md:text-lg">${label}</span>
              ${this.selectedMode === mode ? '<span class="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-yellow-400 rounded-full animate-pulse"></span>' : ''}
            </button>
          `).join('')}
        </div>
        ${this.renderModeOptions()}
        ${this.renderDifficultySelector()}
        ${this.renderFontSizeSelector()}
      </div>
    `;
        // Attach event listeners — use closest so icon/label clicks resolve the button
        this.container.querySelectorAll('.mode-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const button = e.target.closest('.mode-btn');
                if (!button)
                    return;
                const mode = button.getAttribute('data-mode');
                if (!mode)
                    return;
                if (mode === 'paragraphs') {
                    this.onConfigChange({ dictionary: 'reaalipoiss-paragraphs', wordCount: 1 });
                }
                else if (mode === 'time') {
                    this.onConfigChange({ dictionary: 'reaalipoiss-newspaper' });
                }
                else if (mode === 'words') {
                    this.onConfigChange({ dictionary: 'reaalipoiss-newspaper', wordCount: 10 });
                }
                this.onModeChange(mode);
            });
        });
        if (this.selectedMode === 'time') {
            this.container.querySelectorAll('.duration-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const button = e.target.closest('.duration-btn');
                    if (button) {
                        const duration = parseInt(button.getAttribute('data-duration') || '30');
                        this.onConfigChange({ duration });
                    }
                });
            });
        }
        if (this.selectedMode === 'words') {
            this.container.querySelectorAll('.word-count-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const button = e.target.closest('.word-count-btn');
                    if (button) {
                        const wordCount = parseInt(button.getAttribute('data-count') || '25');
                        this.onConfigChange({ wordCount });
                    }
                });
            });
        }
        if (this.selectedMode === 'paragraphs') {
            this.container.querySelectorAll('.paragraph-count-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const button = e.target.closest('.paragraph-count-btn');
                    if (button) {
                        const wordCount = parseInt(button.getAttribute('data-count') || '1');
                        this.onConfigChange({ wordCount, dictionary: 'reaalipoiss-paragraphs' });
                    }
                });
            });
        }
        // Difficulty selector event listeners
        this.container.querySelectorAll('.difficulty-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                // Get the button element (in case click is on child element)
                const button = e.target.closest('.difficulty-btn');
                if (button) {
                    const difficulty = button.getAttribute('data-difficulty');
                    if (difficulty) {
                        this.onConfigChange({ difficulty });
                    }
                }
            });
        });
        // Font size selector event listeners
        this.container.querySelectorAll('.font-size-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const button = e.target.closest('.font-size-btn');
                if (button) {
                    const fontSize = button.getAttribute('data-font-size');
                    if (fontSize) {
                        this.onConfigChange({ fontSize });
                    }
                }
            });
        });
    }
    renderModeOptions() {
        if (this.selectedMode === 'time') {
            return `
        <div class="flex items-center gap-2 md:gap-3 animate-fade-in-up justify-center md:justify-start flex-wrap">
          ${[15, 30, 60, 120, 180, 300].map(duration => `
            <button class="duration-btn group px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 focus:outline-none font-medium min-h-[44px] min-w-[44px] touch-manipulation ${this.config.duration === duration
                ? 'text-yellow-400 bg-yellow-400/10 border-2 border-yellow-400/30 shadow-md shadow-yellow-400/10 scale-105'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50 border-2 border-transparent hover:border-gray-700 hover:scale-105 active:scale-95'}" 
                    data-duration="${duration}"
                    aria-label="${duration} sekundit">
              <span class="relative text-base md:text-lg">${duration}</span>
              ${this.config.duration === duration ? '<span class="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></span>' : ''}
            </button>
          `).join('')}
        </div>
      `;
        }
        if (this.selectedMode === 'words') {
            return `
        <div class="flex items-center gap-2 md:gap-3 animate-fade-in-up justify-center md:justify-start flex-wrap">
          ${[10, 25, 50, 100, 200, 500].map(count => `
            <button class="word-count-btn group px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 focus:outline-none font-medium min-h-[44px] min-w-[44px] touch-manipulation ${this.config.wordCount === count
                ? 'text-yellow-400 bg-yellow-400/10 border-2 border-yellow-400/30 shadow-md shadow-yellow-400/10 scale-105'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50 border-2 border-transparent hover:border-gray-700 hover:scale-105 active:scale-95'}" 
                    data-count="${count}"
                    aria-label="${count} sõna">
              <span class="relative text-base md:text-lg">${count}</span>
              ${this.config.wordCount === count ? '<span class="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></span>' : ''}
            </button>
          `).join('')}
        </div>
      `;
        }
        if (this.selectedMode === 'paragraphs') {
            return `
        <div class="flex items-center gap-2 md:gap-3 animate-fade-in-up justify-center md:justify-start flex-wrap">
          ${[1, 2, 3, 5].map(count => `
            <button class="paragraph-count-btn group px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 focus:outline-none font-medium min-h-[44px] min-w-[44px] touch-manipulation ${this.config.wordCount === count
                ? 'text-yellow-400 bg-yellow-400/10 border-2 border-yellow-400/30 shadow-md shadow-yellow-400/10 scale-105'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50 border-2 border-transparent hover:border-gray-700 hover:scale-105 active:scale-95'}" 
                    data-count="${count}"
                    aria-label="${count} lõiku">
              <span class="relative text-base md:text-lg">${count}</span>
              ${this.config.wordCount === count ? '<span class="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></span>' : ''}
            </button>
          `).join('')}
        </div>
      `;
        }
        return '';
    }
    renderDifficultySelector() {
        // Different difficulty ranges for paragraphs vs words/time modes
        const isParagraphs = this.selectedMode === 'paragraphs';
        const difficulties = isParagraphs ? [
            { value: 'easy', label: 'Lihtne', icon: '🟢', range: '50-200 tähemärki' },
            { value: 'medium', label: 'Keskmine', icon: '🟡', range: '201-400 tähemärki' },
            { value: 'hard', label: 'Raske', icon: '🟠', range: '401-600 tähemärki' },
            { value: 'impossible', label: 'Võimatu', icon: '🔴', range: '601+ tähemärki' },
        ] : [
            { value: 'easy', label: 'Lihtne', icon: '🟢', range: '1-4 tähemärki' },
            { value: 'medium', label: 'Keskmine', icon: '🟡', range: '5-8 tähemärki' },
            { value: 'hard', label: 'Raske', icon: '🟠', range: '9-12 tähemärki' },
            { value: 'impossible', label: 'Võimatu', icon: '🔴', range: '13+ tähemärki' },
        ];
        return `
      <div class="flex flex-col gap-2 md:gap-3 animate-fade-in-up">
        <div class="text-sm md:text-base text-gray-400 font-medium px-1">Raskusaste:</div>
        <div class="flex items-center gap-2 md:gap-3 justify-center md:justify-start flex-wrap">
          ${difficulties.map(({ value, label, icon, range }) => `
            <button class="difficulty-btn group relative px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 focus:outline-none flex items-center gap-2 min-h-[44px] touch-manipulation ${this.config.difficulty === value
            ? 'text-yellow-400 bg-yellow-400/10 border-2 border-yellow-400/30 shadow-md shadow-yellow-400/10 scale-105'
            : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50 border-2 border-transparent hover:border-gray-700 hover:scale-105 active:scale-95'}" 
                    data-difficulty="${value}"
                    aria-label="${label} (${range})"
                    title="${label} (${range})">
              <span class="text-lg md:text-xl transition-transform duration-300 group-hover:scale-110">${icon}</span>
              <span class="font-medium text-sm md:text-base">${label}</span>
              ${this.config.difficulty === value ? '<span class="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></span>' : ''}
            </button>
          `).join('')}
        </div>
      </div>
    `;
    }
    renderFontSizeSelector() {
        const fontSizes = [
            { value: 'small', label: 'Väike', icon: 'Aa' },
            { value: 'medium', label: 'Keskmine', icon: 'Aa' },
            { value: 'large', label: 'Suur', icon: 'Aa' },
            { value: 'xlarge', label: 'Väga suur', icon: 'Aa' },
        ];
        return `
      <div class="flex flex-col gap-2 md:gap-3 animate-fade-in-up">
        <div class="text-sm md:text-base text-gray-400 font-medium px-1">Fondi suurus:</div>
        <div class="flex items-center gap-2 md:gap-3 justify-center md:justify-start flex-wrap">
          ${fontSizes.map(({ value, label, icon }) => `
            <button class="font-size-btn group relative px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 focus:outline-none flex items-center gap-2 min-h-[44px] touch-manipulation ${this.config.fontSize === value
            ? 'text-yellow-400 bg-yellow-400/10 border-2 border-yellow-400/30 shadow-md shadow-yellow-400/10 scale-105'
            : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50 border-2 border-transparent hover:border-gray-700 hover:scale-105 active:scale-95'}" 
                    data-font-size="${value}"
                    aria-label="${label}"
                    title="${label}">
              <span class="font-mono text-sm md:text-base font-bold">${icon}</span>
              <span class="font-medium text-sm md:text-base">${label}</span>
              ${this.config.fontSize === value ? '<span class="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></span>' : ''}
            </button>
          `).join('')}
        </div>
      </div>
    `;
    }
    renderAdvancedOptions() {
        const options = [
            {
                key: 'blindMode',
                label: 'Pime režiim',
                icon: '👁️',
                description: 'Peida tekst kirjutamise ajal'
            },
            {
                key: 'stopOnError',
                label: 'Peatu vea korral',
                icon: '⏸️',
                description: 'Peatu vigase tähe juures'
            },
            {
                key: 'showCaret',
                label: 'Näita kursorit',
                icon: '|',
                description: 'Näita kursori asukohta'
            },
            {
                key: 'punctuation',
                label: 'Kirjavahemärgid',
                icon: '.,!?',
                description: 'Lisa kirjavahemärgid sõnadesse'
            },
            {
                key: 'numbers',
                label: 'Numbrid',
                icon: '123',
                description: 'Lisa numbreid sõnadesse'
            },
            {
                key: 'soundEnabled',
                label: 'Helid',
                icon: '🔊',
                description: 'Luba heliefektid'
            },
        ];
        return `
      <div class="flex flex-col gap-2 md:gap-3 animate-fade-in-up">
        <div class="text-sm md:text-base text-gray-400 font-medium px-1">Lisavalikud:</div>
        <div class="flex items-center gap-2 md:gap-3 justify-center md:justify-start flex-wrap">
          ${options.map(({ key, label, icon, description }) => `
            <button class="option-toggle-btn group relative px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 focus:outline-none flex items-center gap-2 min-h-[44px] touch-manipulation ${this.config[key]
            ? 'text-yellow-400 bg-yellow-400/10 border-2 border-yellow-400/30 shadow-md shadow-yellow-400/10 scale-105'
            : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50 border-2 border-transparent hover:border-gray-700 hover:scale-105 active:scale-95'}" 
                    data-option="${key}"
                    aria-label="${label}"
                    title="${description}">
              <span class="text-sm md:text-base">${icon}</span>
              <span class="font-medium text-xs md:text-sm">${label}</span>
              ${this.config[key] ? '<span class="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></span>' : ''}
            </button>
          `).join('')}
        </div>
      </div>
    `;
    }
    getDefaultConfig() {
        return {
            mode: 'time',
            duration: 30,
            wordCount: 10, // Default word count for words mode
            dictionary: 'reaalipoiss-newspaper',
            difficulty: 'medium',
            blindMode: false,
            layout: 'qwerty',
            fontSize: 'medium',
            stopOnError: false,
            showCaret: true,
            punctuation: false,
            numbers: false,
            soundEnabled: false,
        };
    }
}
